import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
	mdiMagnify,
	mdiFilterVariant,
	mdiChevronDown,
	mdiSortAscending,
	mdiSortDescending,
	mdiDotsVertical,
	mdiAccountGroup,
	mdiClose,
	mdiChevronUp,
	mdiPuzzleOutline,
	mdiStar,
	mdiStarOutline,
} from "@mdi/js";
import { Button, CircularProgress, IconButton, LinearProgress, Tooltip } from "@mui/material";
import { MainLayout } from "layouts";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { NotistackServices, WorkspaceServices, PublicWorkspaceDTO, PublicTeamMemberDTO, PublicTeamDTO, WSServices } from "shared/services";
import { HandleError } from "shared/utils";
import { SeniorityLevel } from "shared/services/McpLibraryServices";

type TabFilter = "all" | "available" | "on_mission" | "favorites";

const TABS: { key: TabFilter; label: string }[] = [
	{ key: "all", label: "Todos os Membros" },
	{ key: "available", label: "Disponíveis" },
	{ key: "on_mission", label: "Em Missão" },
	{ key: "favorites", label: "Favoritos" },
];

const SENIORITY_OPTIONS: { value: SeniorityLevel | "all"; label: string }[] = [
	{ value: "all", label: "Todas" },
	{ value: "junior", label: "Júnior" },
	{ value: "pleno", label: "Pleno" },
	{ value: "senior", label: "Sênior" },
];

function seniorityLabel(level: SeniorityLevel): string {
	switch (level) {
		case "junior":
			return "Júnior";
		case "pleno":
			return "Pleno";
		case "senior":
			return "Sênior";
		default:
			return level;
	}
}

function seniorityColor(level: SeniorityLevel): string {
	switch (level) {
		case "junior":
			return "#4ade80";
		case "pleno":
			return "#60a5fa";
		case "senior":
			return "#c084fc";
		default:
			return "#888";
	}
}

/** Gera uma carga de trabalho simulada baseada no id do membro (sem campo na API) */
function simulateWorkload(memberId: number): number {
	// Deterministic pseudo-random based on memberId
	return ((memberId * 37 + 13) % 100) + 1;
}

export const MyTeamsPage = () => {
	const [workspaces, setWorkspaces] = useState<PublicWorkspaceDTO[]>([]);
	const [selectedWorkspaceUid, setSelectedWorkspaceUid] = useState<string | null>(null);
	const [members, setMembers] = useState<PublicTeamMemberDTO[]>([]);
	const [teams, setTeams] = useState<PublicTeamDTO[]>([]);
	const [loadingWorkspaces, setLoadingWorkspaces] = useState(true);
	const [loadingMembers, setLoadingMembers] = useState(false);

	const [activeTab, setActiveTab] = useState<TabFilter>("all");
	const [seniorityFilter, setSeniorityFilter] = useState<SeniorityLevel | "all">("all");
	const [squadFilter, setSquadFilter] = useState<string>("all");
	const [sortAsc, setSortAsc] = useState(true);
	const [search, setSearch] = useState("");

	const [selectedMember, setSelectedMember] = useState<PublicTeamMemberDTO | null>(null);
	const [favorites, setFavorites] = useState<Set<number>>(new Set());

	const [showSeniorityDropdown, setShowSeniorityDropdown] = useState(false);
	const [showSquadDropdown, setShowSquadDropdown] = useState(false);

	const timeRef = useRef<NodeJS.Timeout>(undefined);

	// Load teams
	const loadWorkspaces = useCallback(() => {
		setLoadingWorkspaces(true);
		clearTimeout(timeRef.current);
		timeRef.current = setTimeout(async () => {
			try {
				const data = await WorkspaceServices.listWorkspaces();
				setWorkspaces(data);
				if (data.length > 0 && !selectedWorkspaceUid) {
					setSelectedWorkspaceUid(data[0].uid);
				}
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar workspaces");
			} finally {
				setLoadingWorkspaces(false);
			}
		}, 300);
	}, []);

	useEffect(() => {
		loadWorkspaces();
	}, [loadWorkspaces]);

	// Load members and equipes when team changes
	useEffect(() => {
		if (!selectedWorkspaceUid) return;
		setLoadingMembers(true);
		const timer = setTimeout(async () => {
			try {
				const [data, teamsData] = await Promise.all([
					WorkspaceServices.getMembers(selectedWorkspaceUid),
					WorkspaceServices.listTeams(selectedWorkspaceUid),
				]);
				setMembers(data);
				setTeams(teamsData);
				if (data.length > 0) {
					setSelectedMember(data[0]);
				} else {
					setSelectedMember(null);
				}
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar membros");
			} finally {
				setLoadingMembers(false);
			}
		}, 200);
		return () => clearTimeout(timer);
	}, [selectedWorkspaceUid]);

	const selectedWorkspace = useMemo(() => workspaces.find((t) => t.uid === selectedWorkspaceUid) ?? null, [workspaces, selectedWorkspaceUid]);

	// WebSocket: escuta atualizações de membros em tempo real (por equipe)
	useEffect(() => {
		if (teams.length === 0) return;

		const subscriptions = teams.map((team) => {
			const eventName = `team:${team.id}:members`;
			return WSServices.onReceive<{ type: string; member?: PublicTeamMemberDTO; memberId?: number }>(eventName, (data) => {
				if (data.type === "member_hired" && data.member) {
					setMembers((prev) => [...prev, data.member!]);
				} else if (data.type === "member_removed" && data.memberId) {
					setMembers((prev) => prev.filter((m) => m.id !== data.memberId));
					setSelectedMember((prev) => (prev?.id === data.memberId ? null : prev));
				}
			});
		});

		return () => subscriptions.forEach((s) => s.stop());
	}, [teams]);

	// Unique categories (squads) from members
	const squads = useMemo(() => {
		const cats = new Set(members.map((m) => m.template?.category).filter(Boolean) as string[]);
		return ["all", ...Array.from(cats).sort()];
	}, [members]);

	const activeCount = useMemo(() => members.filter((m) => m.isActive).length, [members]);

	// Filtered members
	const filteredMembers = useMemo(() => {
		let list = [...members];

		// Tab filter
		if (activeTab === "available") {
			list = [...members];
		} else if (activeTab === "on_mission") {
			list = [];
		} else if (activeTab === "favorites") {
			list = [];
		}

		// Seniority filter
		if (seniorityFilter !== "all") {
			list = list.filter((m) => m.seniority === seniorityFilter);
		}

		// Squad (category) filter
		if (squadFilter !== "all") {
			list = list.filter((m) => m.template?.category === squadFilter);
		}

		// Search
		if (search.trim()) {
			const q = search.toLowerCase();
			list = list.filter(
				(m) =>
					m.name.toLowerCase().includes(q) ||
					(m.template?.category ?? "").toLowerCase().includes(q) ||
					(m.template?.description ?? "").toLowerCase().includes(q),
			);
		}

		// Sort by name
		list.sort((a, b) => {
			const cmp = a.name.localeCompare(b.name);
			return sortAsc ? cmp : -cmp;
		});

		return list;
	}, [members, activeTab, seniorityFilter, squadFilter, search, sortAsc, favorites]);

	const toggleFavorite = (id: number) => {
		setFavorites((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const handlePromoteSeniority = () => {
		NotistackServices.showWarningMessage("Funcionalidade em desenvolvimento");
	};

	// ----- Render -----

	if (loadingWorkspaces) {
		return (
			<MainLayout>
				<div className={styles.loadingState}>
					<CircularProgress
						color="inherit"
						size={24}
					/>
					<span>Carregando workspaces...</span>
				</div>
			</MainLayout>
		);
	}

	if (workspaces.length === 0) {
		return (
			<MainLayout>
				<div className={styles.loadingState}>
					<Icon
						path={mdiAccountGroup}
						size={2}
					/>
					<span>Você ainda não possui workspaces.</span>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<div className={styles.container}>
				{/* Main panel (table) */}
				<div className={styles.mainPanel}>
					{/* Header */}
					<div className={styles.header}>
						<div className={styles.titleRow}>
							<h2 className={styles.title}>Meus Membros</h2>
							<span className={styles.subtitle}>
								{activeCount}/{members.length} agentes ativos
							</span>
						</div>

						{/* Workspace selector */}
						{workspaces.length > 0 && (
							<div className={styles.teamSelector}>
								{workspaces.map((ws) => (
									<Button
										key={ws.uid}
										className={clsx(styles.teamChip, selectedWorkspaceUid === ws.uid && styles.active)}
										onClick={() => setSelectedWorkspaceUid(ws.uid)}
										disableRipple
									>
										{ws.name}
									</Button>
								))}
							</div>
						)}
					</div>

					{/* Tabs */}
					<div className={styles.tabs}>
						{TABS.map((tab) => (
							<button
								key={tab.key}
								className={clsx(styles.tab, activeTab === tab.key && styles.active)}
								onClick={() => setActiveTab(tab.key)}
							>
								{tab.label}
							</button>
						))}
					</div>

					{/* Filters toolbar */}
					<div className={styles.filtersToolbar}>
						<div className={styles.filterGroup}>
							{/* Seniority dropdown */}
							<div className={styles.dropdownWrapper}>
								<button
									className={styles.filterBtn}
									onClick={() => {
										setShowSeniorityDropdown((v) => !v);
										setShowSquadDropdown(false);
									}}
								>
									Senioridade
									<Icon
										path={showSeniorityDropdown ? mdiChevronUp : mdiChevronDown}
										size={0.7}
									/>
								</button>
								{showSeniorityDropdown && (
									<div className={styles.dropdown}>
										{SENIORITY_OPTIONS.map((opt) => (
											<button
												key={opt.value}
												className={clsx(styles.dropdownItem, seniorityFilter === opt.value && styles.active)}
												onClick={() => {
													setSeniorityFilter(opt.value);
													setShowSeniorityDropdown(false);
												}}
											>
												{opt.label}
											</button>
										))}
									</div>
								)}
							</div>

							{/* Squad dropdown */}
							<div className={styles.dropdownWrapper}>
								<button
									className={styles.filterBtn}
									onClick={() => {
										setShowSquadDropdown((v) => !v);
										setShowSeniorityDropdown(false);
									}}
								>
									Squad
									<Icon
										path={showSquadDropdown ? mdiChevronUp : mdiChevronDown}
										size={0.7}
									/>
								</button>
								{showSquadDropdown && (
									<div className={styles.dropdown}>
										{squads.map((squad) => (
											<button
												key={squad}
												className={clsx(styles.dropdownItem, squadFilter === squad && styles.active)}
												onClick={() => {
													setSquadFilter(squad);
													setShowSquadDropdown(false);
												}}
											>
												{squad === "all" ? "Todos" : squad}
											</button>
										))}
									</div>
								)}
							</div>
						</div>

						<div className={styles.filterGroup}>
							{/* Search */}
							<div className={styles.searchBox}>
								<Icon
									path={mdiMagnify}
									size={0.8}
								/>
								<input
									type="text"
									placeholder="Buscar membro..."
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
								{search && (
									<button
										className={styles.clearBtn}
										onClick={() => setSearch("")}
									>
										<Icon
											path={mdiClose}
											size={0.6}
										/>
									</button>
								)}
							</div>

							{/* Sort toggle */}
							<Tooltip title={sortAsc ? "Ordenar Z-A" : "Ordenar A-Z"}>
								<IconButton
									size="small"
									onClick={() => setSortAsc((v) => !v)}
									className={styles.sortBtn}
								>
									<Icon
										path={sortAsc ? mdiSortAscending : mdiSortDescending}
										size={0.9}
									/>
								</IconButton>
							</Tooltip>
						</div>
					</div>

					{/* Table */}
					{loadingMembers ? (
						<div className={styles.loadingState}>
							<CircularProgress
								color="inherit"
								size={24}
							/>
							<span>Carregando membros...</span>
						</div>
					) : filteredMembers.length === 0 ? (
						<div className={styles.loadingState}>
							<Icon
								path={mdiAccountGroup}
								size={2}
							/>
							<span>Nenhum membro encontrado.</span>
						</div>
					) : (
						<div className={styles.tableWrapper}>
							<table className={styles.membersTable}>
								<thead>
									<tr>
										<th className={styles.colMember}>Membro</th>
										<th className={styles.colSeniority}>Senioridade</th>
										<th className={styles.colWorkspace}>Workspace Atual</th>
										<th className={styles.colWorkload}>Carga de Trabalho</th>
										<th className={styles.colActions}>Ações</th>
									</tr>
								</thead>
								<tbody>
									{filteredMembers.map((member) => {
										const workload = simulateWorkload(member.id);
										const isSelected = selectedMember?.id === member.id;

										return (
											<tr
												key={member.id}
												className={clsx(styles.memberRow, isSelected && styles.selected)}
												onClick={() => setSelectedMember(member)}
											>
												<td className={styles.colMember}>
													<div className={styles.memberCell}>
														<div className={styles.memberAvatar}>
															{member.template?.iconUrl ? (
																<img
																	src={member.template.iconUrl}
																	alt={member.name}
																/>
															) : (
																<Icon
																	path={mdiPuzzleOutline}
																	size={1}
																/>
															)}
															<span
																className={clsx(styles.statusDot, member.isActive ? styles.online : styles.offline)}
															/>
														</div>
														<div className={styles.memberInfo}>
															<span className={styles.memberName}>{member.name}</span>
															<span className={styles.memberRole}>
																{member.template?.description || member.template?.category || "Agente IA"}
															</span>
														</div>
													</div>
												</td>
												<td className={styles.colSeniority}>
													<span
														className={styles.seniorityBadge}
														style={{
															backgroundColor: seniorityColor(member.seniority) + "22",
															color: seniorityColor(member.seniority),
														}}
													>
														{seniorityLabel(member.seniority)}
													</span>
												</td>
												<td className={styles.colWorkspace}>
													<span className={styles.workspaceName}>{selectedWorkspace?.name ?? "—"}</span>
												</td>
												<td className={styles.colWorkload}>
													<div className={styles.workloadCell}>
														<LinearProgress
															variant="determinate"
															value={workload}
															className={styles.workloadBar}
															sx={{
																"backgroundColor": "#333",
																"& .MuiLinearProgress-bar": {
																	backgroundColor:
																		workload >= 80 ? "#ef4444" : workload >= 50 ? "#f59e0b" : "#4ade80",
																},
															}}
														/>
														<span className={styles.workloadValue}>{workload}%</span>
													</div>
												</td>
												<td className={styles.colActions}>
													<Tooltip title={favorites.has(member.id) ? "Remover favorito" : "Favoritar"}>
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation();
																toggleFavorite(member.id);
															}}
															className={styles.actionBtn}
														>
															<Icon
																path={favorites.has(member.id) ? mdiStar : mdiStarOutline}
																size={0.8}
																color={favorites.has(member.id) ? "#f59e0b" : undefined}
															/>
														</IconButton>
													</Tooltip>
													<Tooltip title="Opções">
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation();
																NotistackServices.showWarningMessage("Em desenvolvimento");
															}}
															className={styles.actionBtn}
														>
															<Icon
																path={mdiDotsVertical}
																size={0.8}
															/>
														</IconButton>
													</Tooltip>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>

				{/* Right detail sidebar */}
				{selectedMember && (
					<aside className={styles.detailPanel}>
						<div className={styles.detailHeader}>
							<div className={styles.detailAvatar}>
								{selectedMember.template?.iconUrl ? (
									<img
										src={selectedMember.template.iconUrl}
										alt={selectedMember.name}
									/>
								) : (
									<div className={styles.detailAvatarPlaceholder}>
										<Icon
											path={mdiPuzzleOutline}
											size={2.5}
										/>
									</div>
								)}
							</div>
							<h3 className={styles.detailName}>{selectedMember.name}</h3>
							<span className={styles.detailRole}>
								{selectedMember.template?.description || selectedMember.template?.category || "Agente IA"}
							</span>

							<div className={styles.detailTags}>
								<span className={styles.detailTag}>{selectedMember.template?.category ?? "General"}</span>
								<span
									className={styles.detailTag}
									style={{
										backgroundColor: seniorityColor(selectedMember.seniority) + "22",
										color: seniorityColor(selectedMember.seniority),
									}}
								>
									{seniorityLabel(selectedMember.seniority)}
								</span>
							</div>
						</div>

						{/* Bio */}
						<div className={styles.detailSection}>
							<h4 className={styles.sectionTitle}>Sobre</h4>
							<p className={styles.detailBio}>
								{selectedMember.template?.description ||
									selectedMember.customSystemPrompt?.slice(0, 200) ||
									"Sem descrição disponível."}
							</p>
						</div>

						{/* MCPs */}
						{(selectedMember.customAllowedMcps?.length ?? 0) > 0 && (
							<div className={styles.detailSection}>
								<h4 className={styles.sectionTitle}>MCPs</h4>
								<div className={styles.mcpGrid}>
									{(selectedMember.customAllowedMcps ?? []).map((mcp, i) => (
										<div
											key={i}
											className={styles.mcpItem}
										>
											<Icon
												path={mdiPuzzleOutline}
												size={0.8}
											/>
											<span>{mcp}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Competencies */}
						{(selectedMember.customCompetencies?.length ?? selectedMember.template?.competencies?.length ?? 0) > 0 && (
							<div className={styles.detailSection}>
								<h4 className={styles.sectionTitle}>Competências</h4>
								<div className={styles.competenciesGrid}>
									{(selectedMember.customCompetencies ?? selectedMember.template?.competencies ?? []).map((comp, i) => (
										<span
											key={i}
											className={styles.competencyTag}
										>
											{comp}
										</span>
									))}
								</div>
							</div>
						)}

						{/* Performance bars */}
						<div className={styles.detailSection}>
							<h4 className={styles.sectionTitle}>Performance</h4>
							<div className={styles.performanceBars}>
								<div className={styles.perfItem}>
									<div className={styles.perfLabel}>
										<span>Qualidade</span>
										<span>85%</span>
									</div>
									<LinearProgress
										variant="determinate"
										value={85}
										sx={{
											"height": 6,
											"borderRadius": 3,
											"backgroundColor": "#333",
											"& .MuiLinearProgress-bar": { backgroundColor: "#4ade80", borderRadius: 3 },
										}}
									/>
								</div>
								<div className={styles.perfItem}>
									<div className={styles.perfLabel}>
										<span>Velocidade</span>
										<span>72%</span>
									</div>
									<LinearProgress
										variant="determinate"
										value={72}
										sx={{
											"height": 6,
											"borderRadius": 3,
											"backgroundColor": "#333",
											"& .MuiLinearProgress-bar": { backgroundColor: "#60a5fa", borderRadius: 3 },
										}}
									/>
								</div>
								<div className={styles.perfItem}>
									<div className={styles.perfLabel}>
										<span>Consistência</span>
										<span>90%</span>
									</div>
									<LinearProgress
										variant="determinate"
										value={90}
										sx={{
											"height": 6,
											"borderRadius": 3,
											"backgroundColor": "#333",
											"& .MuiLinearProgress-bar": { backgroundColor: "#c084fc", borderRadius: 3 },
										}}
									/>
								</div>
							</div>
						</div>

						{/* Promote button */}
						{selectedMember.seniority !== "senior" && (
							<Button
								className={styles.promoteBtn}
								onClick={handlePromoteSeniority}
								fullWidth
								disableRipple
							>
								<Icon
									path={mdiChevronUp}
									size={0.9}
								/>
								Promover Senioridade
							</Button>
						)}
					</aside>
				)}
			</div>
		</MainLayout>
	);
};
